import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import { getFileUrl } from '../utils/fileUtil';
import TemplateGallery from '../components/templates/TemplateGallery';

const AdminPanel = () => {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'templates' | 'brand'
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'member', isAgent: false, isLocalAgent: false, agentId: '', agentBrain: '', agentModel: 'gpt-4o'
  });
  const [editingAgent, setEditingAgent] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateFormData, setTemplateFormData] = useState({
    name: '', descriptionTemplate: '', defaultPriority: 'Medium'
  });

  const [brandSettings, setBrandSettings] = useState({
    appName: '', logo: '', favicon: '', primaryColor: '#1a73e8',
    apiKeys: { openai: '', anthropic: '' },
    enabledModels: []
  });
  const [newModel, setNewModel] = useState({ id: '', name: '', provider: 'openai' });
  const [suggestedModels, setSuggestedModels] = useState([]);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Agent State
  const [generatedKey, setGeneratedKey] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTemplates();
    fetchBrandSettings();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/users', { headers: { 'x-auth-token': token } });
      setUsers(res.data);
      setLoading(false);
    } catch (err) { console.error(err); }
  };

  const handleAdminAvatarUpload = async (userId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('avatar', file);
    try {
      await axios.post(`/api/users/${userId}/avatar`, data, {
        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
      });
      fetchUsers();
    } catch (err) { alert('Avatar upload failed'); }
  };

  const handleImpersonate = async (userId) => {
    if (!window.confirm('Act as this user? You will be logged out of your current session.')) return;
    try {
      const res = await axios.post(`/api/users/${userId}/impersonate`, {}, {
        headers: { 'x-auth-token': token }
      });
      
      // Store the new token and reload
      localStorage.setItem('token', res.data.token);
      window.location.href = '/dashboard';
    } catch (err) {
      alert('Failed to impersonate user');
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/api/templates', { headers: { 'x-auth-token': token } });
      setTemplates(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBrandSettings = async () => {
    try {
      // Admin-only endpoint returns the provider API keys for the form below.
      const res = await axios.get('/api/brand/admin', { headers: { 'x-auth-token': token } });
      setBrandSettings(res.data);
    } catch (err) { console.error(err); }
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/api/brand', brandSettings, { headers: { 'x-auth-token': token } });
      alert('Brand settings updated!');
    } catch (err) { alert('Failed to update brand'); }
  };

  const fetchProviderModels = async () => {
    setFetchingModels(true);
    try {
      const res = await axios.get('/api/ai/models', { headers: { 'x-auth-token': token } });
      setSuggestedModels(res.data);
    } catch (err) {
      alert('Failed to fetch models from providers. Check your API keys.');
    } finally {
      setFetchingModels(false);
    }
  };

  const addModelFromSuggestion = (model) => {
    if (brandSettings.enabledModels.some(m => m.id === model.id)) {
      alert('This model is already in your list.');
      return;
    }
    const updatedModels = [...brandSettings.enabledModels, { ...model, isEnabled: true }];
    setBrandSettings({ ...brandSettings, enabledModels: updatedModels });
  };

  const handleAddModel = () => {
    if (!newModel.id || !newModel.name) return;
    const updatedModels = [...brandSettings.enabledModels, { ...newModel, isEnabled: true }];
    setBrandSettings({ ...brandSettings, enabledModels: updatedModels });
    setNewModel({ id: '', name: '', provider: 'openai' });
  };

  const toggleModel = (index) => {
    const updatedModels = [...brandSettings.enabledModels];
    updatedModels[index].isEnabled = !updatedModels[index].isEnabled;
    setBrandSettings({ ...brandSettings, enabledModels: updatedModels });
  };

  const removeModel = (index) => {
    const updatedModels = brandSettings.enabledModels.filter((_, i) => i !== index);
    setBrandSettings({ ...brandSettings, enabledModels: updatedModels });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('logo', file);
    setUploadingLogo(true);
    try {
      const res = await axios.post('/api/brand/logo', data, {
        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
      });
      setBrandSettings({ ...brandSettings, logo: res.data.logo });
      window.location.reload(); 
    } catch (err) { alert('Logo upload failed'); }
    finally { setUploadingLogo(false); }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('favicon', file);
    setUploadingFavicon(true);
    try {
      const res = await axios.post('/api/brand/favicon', data, {
        headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' }
      });
      setBrandSettings({ ...brandSettings, favicon: res.data.favicon });
      window.location.reload();
    } catch (err) { alert('Favicon upload failed'); }
    finally { setUploadingFavicon(false); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Remove this user?')) return;
    try {
      await axios.delete(`/api/users/${id}`, { headers: { 'x-auth-token': token } });
      setUsers(users.filter(u => u._id !== id));
    } catch (err) { alert('Failed to delete user'); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post('/api/users/admin', formData, { headers: { 'x-auth-token': token } });
      if (res.data.isAgent && !res.data.isLocalAgent) {
        setGeneratedKey(res.data.apiKey);
      } else {
        setShowAddModal(false);
        setFormData({ name: '', email: '', password: '', role: 'member', isAgent: false, isLocalAgent: false, agentId: '', agentBrain: '', agentModel: 'gpt-4o' });
      }
      fetchUsers();
    } catch (err) { alert(err.response?.data?.msg || 'Failed to create user'); }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/users/agents/${editingAgent._id}`, formData, { headers: { 'x-auth-token': token } });
      setShowAddModal(false);
      setEditingAgent(null);
      setFormData({ name: '', email: '', password: '', role: 'member', isAgent: false, isLocalAgent: false, agentId: '', agentBrain: '', agentModel: 'gpt-4o' });
      fetchUsers();
    } catch (err) { alert(err.response?.data?.msg || 'Failed to update agent'); }
  };

  const startEditAgent = (user) => {
    setEditingAgent(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      isAgent: user.isAgent,
      isLocalAgent: user.isLocalAgent,
      agentId: user.agentId || '',
      agentBrain: user.agentBrain || '',
      agentModel: user.agentModel || 'gpt-4o'
    });
    setShowAddModal(true);
  };

  const handleRegenerateKey = async (id) => {
    if (!window.confirm('Generate a new API key? The old one will stop working immediately.')) return;
    try {
      const res = await axios.post(`/api/users/${id}/regenerate-key`, {}, { headers: { 'x-auth-token': token } });
      alert(`New Key: ${res.data.apiKey}`);
      fetchUsers();
    } catch (err) { alert('Failed to regenerate key'); }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/templates', templateFormData, { headers: { 'x-auth-token': token } });
      setShowTemplateModal(false);
      setTemplateFormData({ name: '', descriptionTemplate: '', defaultPriority: 'Medium' });
      fetchTemplates();
    } catch (err) { alert('Failed to create template'); }
  };

  const handleDeleteTemplate = (templateId) => {
    setTemplates(prev => prev.filter(t => t._id !== templateId));
  };

  if (loading) return <div className="p-8 text-center">Loading Admin Panel...</div>;

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 font-sans">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight uppercase">Admin Control Center</h1>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-8 w-fit">
        {['users', 'templates', 'brand'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all uppercase tracking-wider ${activeTab === tab ? 'bg-white dark:bg-gray-700 text-primary-blue shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-fade-in">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex justify-between items-center">
            <h2 className="text-lg font-black text-gray-800 dark:text-gray-100 uppercase">User Management</h2>
            <button onClick={() => { setGeneratedKey(null); setShowAddModal(true); }} className="bg-primary-blue text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md">+ Add User</button>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="relative group/avatar">
                        <div className="h-12 w-12 rounded-full bg-secondary-cyan flex items-center justify-center text-white font-bold overflow-hidden border-2 border-white shadow-sm">
                          {user.avatar ? <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" /> : user.name.charAt(0).toUpperCase()}
                        </div>
                        <label className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 cursor-pointer transition-opacity">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          <input type="file" className="hidden" onChange={(e) => handleAdminAvatarUpload(user._id, e)} accept="image/*" />
                        </label>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <span className="text-sm font-bold text-gray-900 dark:text-gray-100 mr-2">{user.name}</span>
                          {user.isAgent && (
                            <div className="flex space-x-1">
                              <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">🤖 Agent</span>
                              {user.isLocalAgent && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">🏠 Local</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleImpersonate(user._id)}
                        className="text-[10px] font-black uppercase bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-primary-blue hover:text-white transition-all shadow-sm"
                      >
                        🎭 Impersonate
                      </button>
                      {user.isAgent && user.isLocalAgent && (
                        <button 
                          onClick={() => startEditAgent(user)}
                          className="text-[10px] font-black uppercase bg-purple-50 text-purple-600 px-2 py-1 rounded hover:bg-purple-100 transition-all shadow-sm"
                        >
                          ⚙️ Edit Config
                        </button>
                      )}
                      {user.isAgent && !user.isLocalAgent && (
                        <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded border border-gray-100 dark:border-gray-700 flex items-center">
                          <span>••••{user.apiKey?.slice(-4)}</span>
                          <button onClick={() => handleRegenerateKey(user._id)} className="ml-2 text-primary-blue hover:underline">Regen</button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button onClick={() => handleDeleteUser(user._id)} className="text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 font-bold text-xs uppercase tracking-widest transition-all">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-fade-in">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex justify-between items-center">
            <h2 className="text-lg font-black text-gray-800 dark:text-gray-100 uppercase">Card Templates</h2>
            <button onClick={() => setShowTemplateModal(true)} className="bg-primary-blue text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md">+ New Template</button>
          </div>
          <div className="p-6">
            <TemplateGallery
              templates={templates}
              onSelect={null}
              onDelete={handleDeleteTemplate}
            />
          </div>
        </div>
      )}

      {/* Brand Tab */}
      {activeTab === 'brand' && (
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-fade-in">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
            <h2 className="text-lg font-black text-gray-800 dark:text-gray-100 uppercase">Global Branding</h2>
          </div>
          <div className="p-8 max-w-2xl">
            <form onSubmit={handleUpdateBrand} className="space-y-8">
              <div>
                <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Application Name</label>
                <input 
                  type="text" 
                  className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue focus:border-primary-blue text-lg font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={brandSettings.appName}
                  onChange={(e) => setBrandSettings({...brandSettings, appName: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Logo</label>
                  <div className="flex items-center space-x-4">
                    <div className="h-20 w-20 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                      {brandSettings.logo ? <img src={getFileUrl(brandSettings.logo)} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-3xl">🖼️</span>}
                    </div>
                    <label className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100">
                      {uploadingLogo ? 'Uploading...' : 'Change Logo'}
                      <input type="file" className="hidden" onChange={handleLogoUpload} accept="image/*" />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Favicon</label>
                  <div className="h-12 w-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                    {brandSettings.favicon ? <img src={getFileUrl(brandSettings.favicon)} alt="Fav" className="h-full w-full object-contain" /> : <span className="text-xl">🎯</span>}
                  </div>
                  <label className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100">
                    {uploadingFavicon ? 'Uploading...' : 'Change Favicon'}
                    <input type="file" className="hidden" onChange={handleFaviconUpload} accept="image/x-icon,image/png" />
                  </label>
                </div>
              </div>

              <div className="pt-8 border-t space-y-8">
                <h3 className="text-sm font-black text-gray-900 dark:text-gray-100 uppercase tracking-widest">External AI Integrations</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase mb-2">OpenAI API Key</label>
                    <input 
                      type="password" 
                      placeholder="sk-..."
                      className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={brandSettings.apiKeys?.openai || ''}
                      onChange={(e) => setBrandSettings({
                        ...brandSettings, 
                        apiKeys: { ...brandSettings.apiKeys, openai: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">Anthropic API Key</label>
                    <input 
                      type="password" 
                      placeholder="sk-ant-..."
                      className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={brandSettings.apiKeys?.anthropic || ''}
                      onChange={(e) => setBrandSettings({
                        ...brandSettings, 
                        apiKeys: { ...brandSettings.apiKeys, anthropic: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Model Management</h4>
                    <button 
                      type="button"
                      onClick={fetchProviderModels}
                      disabled={fetchingModels}
                      className="text-[10px] font-black uppercase bg-purple-50 text-purple-600 px-3 py-1.5 rounded-lg hover:bg-purple-100 transition-all border border-purple-100"
                    >
                      {fetchingModels ? '📡 Fetching...' : '📡 Fetch Latest Models'}
                    </button>
                  </div>

                  {suggestedModels.length > 0 && (
                    <div className="p-4 bg-purple-50/50 rounded-xl border border-purple-100 animate-fade-in">
                      <p className="text-[10px] font-black text-purple-400 uppercase mb-3">Suggested Models from API</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin">
                        {suggestedModels.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => addModelFromSuggestion(m)}
                            className="text-left p-2 bg-white dark:bg-gray-700 rounded-lg border border-purple-100 dark:border-purple-900 hover:border-purple-400 dark:hover:border-purple-600 transition-all group"
                          >
                            <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200 truncate">{m.name}</p>
                            <p className="text-[8px] text-purple-400 uppercase font-black">{m.provider}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <input 
                        type="text" 
                        placeholder="Model ID (e.g. gpt-4o)" 
                        className="text-xs p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={newModel.id}
                        onChange={(e) => setNewModel({ ...newModel, id: e.target.value })}
                      />
                      <input 
                        type="text" 
                        placeholder="Display Name" 
                        className="text-xs p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={newModel.name}
                        onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                      />
                      <div className="flex space-x-2">
                        <select 
                          className="flex-1 text-xs p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={newModel.provider}
                          onChange={(e) => setNewModel({ ...newModel, provider: e.target.value })}
                        >
                          <option value="openai">OpenAI</option>
                          <option value="anthropic">Anthropic</option>
                        </select>
                        <button 
                          type="button"
                          onClick={handleAddModel}
                          className="bg-gray-900 text-white px-3 py-2 rounded-lg text-xs font-bold"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {brandSettings.enabledModels.map((m, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white dark:bg-gray-700 p-2 px-3 rounded-lg border border-gray-100 dark:border-gray-600">
                          <div className="flex items-center space-x-3">
                            <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${m.provider === 'openai' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                              {m.provider}
                            </span>
                            <span className="text-xs font-bold text-gray-800 dark:text-gray-200">{m.name}</span>
                            <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">{m.id}</span>
                          </div>
                          <div className="flex items-center space-x-4">
                            <button 
                              type="button"
                              onClick={() => toggleModel(idx)}
                              className={`text-[10px] font-black uppercase ${m.isEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}
                            >
                              {m.isEnabled ? 'Enabled' : 'Disabled'}
                            </button>
                            <button 
                              type="button"
                              onClick={() => removeModel(idx)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <button type="submit" className="bg-primary-blue text-white px-8 py-3 rounded-xl font-black uppercase tracking-widest shadow-lg hover:shadow-primary-blue/20">Save All Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in overflow-hidden relative">
            {generatedKey ? (
              <div className="text-center py-4">
                <div className="text-5xl mb-4">🤖</div>
                <h3 className="text-xl font-black uppercase mb-2 dark:text-gray-100">Agent Created!</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Copy this API key now. It will not be shown again.</p>
                <div className="bg-gray-900 text-green-400 p-4 rounded-xl font-mono text-sm break-all mb-6 select-all shadow-inner">
                  {generatedKey}
                </div>
                <button 
                  onClick={() => {
                    setShowAddModal(false);
                    setGeneratedKey(null);
                    setFormData({ name: '', email: '', password: '', role: 'member', isAgent: false, isLocalAgent: false, agentId: '' });
                  }}
                  className="w-full bg-primary-blue text-white py-3 rounded-xl font-black uppercase tracking-widest shadow-lg"
                >
                  I have saved the key
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-black text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-tight">
                  {editingAgent ? `Configure Agent: ${editingAgent.name}` : 'Register New Profile'}
                </h3>
                <form onSubmit={editingAgent ? handleUpdateAgent : handleCreateUser} className="space-y-4">
                  <input required type="text" placeholder="Full Name" className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                  
                  {!editingAgent && (
                    <div className="flex flex-col space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center">
                          <span className="mr-2">🤖</span> AI Agent Account?
                        </label>
                        <input 
                          type="checkbox" 
                          className="h-5 w-5 rounded text-primary-blue"
                          checked={formData.isAgent}
                          onChange={(e) => setFormData({...formData, isAgent: e.target.checked, isLocalAgent: e.target.checked ? formData.isLocalAgent : false})}
                        />
                      </div>
                      {formData.isAgent && (
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600 mt-2">
                          <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 flex items-center">
                            <span className="mr-2">🏠</span> Local Agent (No API)
                          </label>
                          <input 
                            type="checkbox" 
                            className="h-4 w-4 rounded text-blue-500"
                            checked={formData.isLocalAgent}
                            onChange={(e) => setFormData({...formData, isLocalAgent: e.target.checked})}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {!editingAgent && (
                    <input 
                      required={!formData.isAgent}
                      type="email" 
                      placeholder={formData.isAgent ? "Email Address (Optional)" : "Email Address"} 
                      className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={formData.email} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    />
                  )}

                  {formData.isAgent ? (
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-900 animate-fade-in space-y-4">
                      {!formData.isLocalAgent && (
                        <div>
                          <p className="text-[10px] font-black text-purple-700 uppercase mb-1">Agent ID (Optional)</p>
                          <input type="text" placeholder="UUID from OpenClaw" className="w-full bg-white dark:bg-gray-700 border-purple-200 dark:border-purple-800 rounded-lg p-2 text-sm text-gray-900 dark:text-gray-100" value={formData.agentId} onChange={(e) => setFormData({...formData, agentId: e.target.value})} />
                        </div>
                      )}

                      <div>
                        <p className="text-[10px] font-black text-purple-700 uppercase mb-1">Intelligence Model</p>
                        <select
                          className="w-full bg-white dark:bg-gray-700 border-purple-200 dark:border-purple-800 rounded-lg p-2 text-sm text-gray-900 dark:text-gray-100"
                          value={formData.agentModel}
                          onChange={(e) => setFormData({...formData, agentModel: e.target.value})}
                        >
                          {brandSettings.enabledModels.filter(m => m.isEnabled).map(m => (
                            <option key={m.id} value={m.id}>{m.name} ({m.provider})</option>
                          ))}
                          {brandSettings.enabledModels.filter(m => m.isEnabled).length === 0 && (
                            <option value="gpt-4o">GPT-4o (Fallback)</option>
                          )}
                        </select>
                      </div>

                      <div>
                        <p className="text-[10px] font-black text-purple-700 uppercase mb-1">Agent Brain (System Prompt / Behavior)</p>
                        <textarea
                          rows="4"
                          placeholder="You are a helpful assistant that..."
                          className="w-full bg-white dark:bg-gray-700 border-purple-200 dark:border-purple-800 rounded-lg p-2 text-xs font-mono text-gray-900 dark:text-gray-100"
                          value={formData.agentBrain} 
                          onChange={(e) => setFormData({...formData, agentBrain: e.target.value})}
                        ></textarea>
                        <p className="text-[9px] text-purple-400 mt-1 font-medium italic">* This defines the core personality and rules for the agent.</p>
                      </div>
                    </div>
                  ) : (
                    !editingAgent && <input required type="password" placeholder="Initial Password" className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue animate-fade-in bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} />
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => {
                      setShowAddModal(false);
                      setEditingAgent(null);
                      setFormData({ name: '', email: '', password: '', role: 'member', isAgent: false, isLocalAgent: false, agentId: '', agentBrain: '', agentModel: 'gpt-4o' });
                    }} className="text-gray-400 dark:text-gray-500 font-bold uppercase text-xs tracking-widest hover:text-gray-600 dark:hover:text-gray-400">Cancel</button>
                    <button type="submit" className="bg-primary-blue text-white px-6 py-2 rounded-lg font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all shadow-md">
                      {editingAgent ? 'Save Config' : 'Create Profile'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-fade-in">
            <h3 className="text-xl font-black text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-tight">Define Workflow Template</h3>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <input required type="text" placeholder="Template Name (e.g. Creative Brief)" className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={templateFormData.name} onChange={(e) => setTemplateFormData({...templateFormData, name: e.target.value})} />
              <textarea required rows="6" placeholder="Structure Content (Markdown Supported)..." className="w-full border-gray-200 dark:border-gray-600 rounded-xl p-3 focus:ring-primary-blue font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" value={templateFormData.descriptionTemplate} onChange={(e) => setTemplateFormData({...templateFormData, descriptionTemplate: e.target.value})} />
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowTemplateModal(false)} className="text-gray-400 dark:text-gray-500 font-bold uppercase text-xs tracking-widest hover:text-gray-600 dark:hover:text-gray-400">Cancel</button>
                <button type="submit" className="bg-primary-blue text-white px-6 py-2 rounded-lg font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all shadow-md">Save Template</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;